import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import PatientDetailsHeader from '../../../components/Account/PatientDetailsHeader';
import AlertCircle from '../../../components/AlertCircle';
import ComplianceCard from '../../../components/Cards/ComplianceCard/ComplianceCard';
import DetailCard from '../../../components/Cards/DetailCard';
import Scrollable from '../../../components/Scrollable';
import ControlVisibility from '../../../components/Settings/ControlVisibility';
import { useClientTheme } from '../../../helpers/hooks/useClientTheme';
import { ActiveType } from '../../../model/activeType';
import { getActiveOverview } from '../../../redux/actions/activeActions';
import {
  clearComplianceData,
  getComplianceOverview,
} from '../../../redux/actions/complianceActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { activeOverview } from '../../../redux/selectors/activeSelectors';
import { selectIsAdmin } from '../../../redux/selectors/accountSelectors';
import { complianceOverview } from '../../../redux/selectors/complianceSelectors';
import {
  selectHasPatientName,
  selectPatientDetailError,
  selectedPatientDetail,
} from '../../../redux/selectors/patientSelectors';
import DailyMetricsPage from '../../DailyMetricsPage';

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    flexFlow: 'column',
    height: '100vh',
  },
  icon: {
    marginRight: theme.spacing.unit * 2,
  },
  heroContent: {
    marginTop: 30,
    padding: `${theme.spacing.unit * 8}px 0 ${theme.spacing.unit * 6}px`,
  },
  layout: {
    width: 'auto',
    marginLeft: theme.spacing.unit * 3,
    marginRight: theme.spacing.unit * 3,
    [theme.breakpoints.up(1100 + theme.spacing.unit * 3 * 2)]: {
      width: 1100,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  cardGrid: {
    padding: `${theme.spacing.unit * 8}px 0`,
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardMedia: {
    paddingTop: '56.25%', // 16:9
  },
  cardContent: {
    flexGrow: 1,
  },
  footer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing.unit * 6,
  },
}));

const PatientDetail: React.FC = () => {
  const { t } = useTranslation(['patientDetail', 'compliance']);
  const { patientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const { theme } = useClientTheme();
  const classes = useStyles();
  const activeOverviewData = useSelector(activeOverview);
  const complianceOverviewData = useSelector(complianceOverview);
  const isAdmin = useSelector(selectIsAdmin);
  const hasPatientName = useSelector(selectHasPatientName);
  const patientDetail = useSelector(selectedPatientDetail);
  const patientDetailError = useSelector(selectPatientDetailError);

  // Patient detail is still loading — neither success nor error has arrived yet,
  // or stale data from a previously viewed patient is still in Redux.
  const isPatientDetailLoading =
    (!patientDetail && !patientDetailError) ||
    (patientDetail && patientId && patientDetail.accountGuid !== patientId);

  // Hide sections when the API returned an error (e.g. invalid patient ID),
  // when a non-admin user doesn't have access (patient data loaded but no name),
  // or while we're still waiting for the patient detail response.
  const shouldHideSections =
    isPatientDetailLoading ||
    patientDetailError ||
    (!isAdmin && patientDetail && !hasPatientName);

  //this is duplicated in the compliance table page - refactor

  function renderComplianceTooltip(fill?: string) {
    const complianceCircles = [
      {
        fill: theme.palette.complianceNotAchievable.main,
        text: t('compliance:tooltip.notAchievable'),
      },
      {
        fill: theme.palette.complianceAtRisk.main,
        text: t('compliance:tooltip.atRisk'),
      },
      {
        fill: theme.palette.complianceOnTrack.main,
        text: t('compliance:tooltip.onTrack'),
      },
      {
        fill: theme.palette.complianceCompleted.main,
        text: t('compliance:tooltip.completed'),
      },
    ];

    if (fill) {
      const circle = complianceCircles.find((circle) => circle.fill === fill);
      return circle ? circle.text : '';
    }

    return (
      <Grid container>
        <Grid size={12} sx={{ mt: 0.5 }}>
          {t('compliance:tooltip.intro')}
        </Grid>
        {complianceCircles.map((circle, index) => (
          <React.Fragment key={index}>
            <Grid size={3}


              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '5px 0',
              }}
            >
              <AlertCircle fill={circle.fill} />
            </Grid>
            <Grid size={7} style={{ margin: '5px 0' }}>
              {circle.text}
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    );
  }

  useEffect(() => {
    // Only fetch active/compliance data after patient detail has loaded successfully.
    // This prevents unnecessary 400 errors when the patient ID is invalid.
    if (patientId && patientDetail && !patientDetailError) {
      dispatch(getActiveOverview(patientId));
      dispatch(getComplianceOverview(patientId));
    }

    //clearComplianceData
    return () => {
      dispatch(clearComplianceData());
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, patientDetail, patientDetailError]);

  const handleClick = (e: any) => {
    navigate(`/dashboard/patients/${patientId}/active/${e}`);
  };

  const handleComplianceClick = () => {
    navigate(`/dashboard/patients/${patientId}/other/compliance`);
  };

  return (
    <div className={classes.root}>
      <Scrollable>
        <PatientDetailsHeader selectedPatientId={patientId} />

        {!shouldHideSections && (
          <>
            <DailyMetricsPage selectedPatientId={patientId} />

            <>
              <div className={classes.heroContent}>
                <Typography variant="h5" align="left" gutterBottom>
                  <strong>{t('sectionOther')}</strong>
                </Typography>
              </div>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <ControlVisibility value="latestCustom">
                  {activeOverviewData?.latestCustom ? (
                    <Grid>
                      <DetailCard
                        title={ActiveType['_CTEST']}
                        icon="_TUGT"
                        onClick={() => handleClick('_CTEST')}
                        popoverContent={t('cards.custom.popover')}
                        content={t('cards.custom.content')}
                        mostRecentDate={activeOverviewData?.latestCustom?.dateTime}
                      />
                    </Grid>
                  ) : null}
                </ControlVisibility>
                <ControlVisibility value="latest10MWT">
                  {activeOverviewData?.latest10MWT ? (
                    <Grid>
                      <DetailCard
                        title={ActiveType['_10MWT']}
                        icon="_10MWT"
                        onClick={() => handleClick('_10MWT')}
                        popoverContent={t('cards.tenMetreWalk.popover')}
                        content={activeOverviewData.latest10MWT}
                      />
                    </Grid>
                  ) : null}
                </ControlVisibility>
                <ControlVisibility value="latestTUG">
                  {activeOverviewData?.latestTUG ? (
                    <Grid>
                      <DetailCard
                        title={ActiveType['_TUG']}
                        icon="_TUGT"
                        onClick={() => handleClick('_TUG')}
                        popoverContent={t('cards.timedUpAndGo.popover')}
                        content={activeOverviewData?.latestTUG}
                      />
                    </Grid>
                  ) : null}
                </ControlVisibility>
                <ControlVisibility value="latestCompliance">
                  <Grid>
                    <ComplianceCard
                      large
                      title={t('compliance:title')}
                      onClick={() => handleComplianceClick()}
                      data={complianceOverviewData}
                      popoverContent={renderComplianceTooltip()}
                    />
                  </Grid>
                </ControlVisibility>
              </Grid>
            </>
          </>
        )}
      </Scrollable>
    </div>
  );
};

export default PatientDetail;
